import { type ReactElement, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBatchesByInput } from "@/features/inventory/hooks/useBatches";
import { InputLookup } from "@/features/inventory/components/inputs/InputLookup";
import type { Supplier } from "@/features/inventory/services/suppliersApi";

interface GenerateFromBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSupplierId?: string;
  suppliers: Supplier[];
  isSubmitting?: boolean;
  onGenerate: (batchIds: string[]) => void;
}

export const GenerateFromBatchDialog = ({
  open,
  onOpenChange,
  initialSupplierId,
  suppliers,
  isSubmitting = false,
  onGenerate,
}: GenerateFromBatchDialogProps): ReactElement => {
  const [supplierId, setSupplierId] = useState("");
  const [inputId, setInputId] = useState("");
  const [inputLabel, setInputLabel] = useState("");
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  const batchesQuery = useBatchesByInput(inputId);

  useEffect(() => {
    if (open) {
      setSupplierId(initialSupplierId ?? "");
      setInputId("");
      setInputLabel("");
      setSelectedBatchIds([]);
    }
  }, [open, initialSupplierId]);

  useEffect(() => {
    setSelectedBatchIds([]);
  }, [inputId]);

  const toggleBatch = (batchId: string, checked: boolean) => {
    setSelectedBatchIds((current) => {
      if (checked) {
        if (current.includes(batchId)) {
          return current;
        }
        return [...current, batchId];
      }

      return current.filter((id) => id !== batchId);
    });
  };

  const selectableBatches = (batchesQuery.data ?? []).filter((batch) => batch.isActive);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Generate Supplier Invoice</DialogTitle>
          <DialogDescription>
            Select supplier, input, then choose one or more batches to generate a supplier invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="supplier-id">Supplier</Label>
              <Select
                value={supplierId}
                onValueChange={(value) => {
                  setSupplierId(value);
                  setInputId("");
                  setInputLabel("");
                }}
              >
                <SelectTrigger id="supplier-id">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.supplierName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="input-id">Input</Label>
              <InputLookup value={inputId} selectedLabel={inputLabel} onSelect={(input) => { setInputId(input.id); setInputLabel(`${input.inputCode} — ${input.inputName}`); }} supplierId={supplierId} disabled={!supplierId} placeholder={supplierId ? "Select input" : "Select supplier first"} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Available Batches</Label>
            <div className="max-h-72 overflow-y-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>Batch Number</TableHead>
                    <TableHead>Lot Number</TableHead>
                    <TableHead>Received Qty</TableHead>
                    <TableHead>Received Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inputId && batchesQuery.data?.map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedBatchIds.includes(batch.id)}
                          onCheckedChange={(checked) =>
                            toggleBatch(batch.id, checked === true)
                          }
                          disabled={!batch.isActive || isSubmitting}
                        />
                      </TableCell>
                      <TableCell>{batch.batchNumber}</TableCell>
                      <TableCell>{batch.lotNumber}</TableCell>
                      <TableCell>{batch.receivedQuantity}</TableCell>
                      <TableCell>{new Date(batch.receivedDate).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {!inputId && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        Select supplier and input to load batches.
                      </TableCell>
                    </TableRow>
                  )}
                  {inputId && batchesQuery.isLoading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        <LoadingAnimation text="Loading batches..." />
                      </TableCell>
                    </TableRow>
                  )}
                  {inputId && !batchesQuery.isLoading && !selectableBatches.length && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No active batches found for selected input.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <Button
              onClick={() => onGenerate(selectedBatchIds)}
              disabled={isSubmitting || !selectedBatchIds.length}
            >
              {isSubmitting
                ? "Generating..."
                : `Generate ${selectedBatchIds.length > 1 ? "from selected batches" : "from selected batch"}`}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
